/**
 * Reminders Landing Page Component
 * Shown to unauthenticated users on /my-reminders/
 * Explains the SMS reminders feature and provides inline sign-up.
 */

import { useState } from '@wordpress/element';
import { useAuth } from '../context/AuthContext';

export default function RemindersLanding({ onAuthSuccess }) {
    const { requestCode, verifyCode, error } = useAuth();
    const [step, setStep] = useState('register'); // 'register' or 'code'
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState('');

    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, '');
        if (digits.length <= 3) {
            return digits;
        } else if (digits.length <= 6) {
            return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        } else {
            return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
        }
    };

    const handlePhoneChange = (e) => {
        setPhone(formatPhoneNumber(e.target.value));
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setLoading(true);

        try {
            await requestCode(phone.replace(/\s/g, ''), fullName.trim(), email.trim());
            setStep('code');
        } catch (err) {
            setLocalError(err.message || 'Failed to send code');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setLoading(true);

        try {
            const result = await verifyCode(phone.replace(/\s/g, ''), code, rememberMe);
            if (result.success) {
                onAuthSuccess();
            }
        } catch (err) {
            setLocalError(err.message || 'Invalid code');
        } finally {
            setLoading(false);
        }
    };

    const isPhoneValid = phone.replace(/\s/g, '').length >= 9;
    const isNameValid = fullName.trim().length >= 2;

    return (
        <div className="reminders-landing">
            {/* Hero */}
            <div className="reminders-landing__hero">
                <svg className="reminders-landing__bell-icon" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                <h1 className="reminders-landing__title">Never Miss a Film</h1>
                <p className="reminders-landing__subtitle">
                    Get a text message before your favourite films screen on Rialto Channel, so you never miss a moment.
                </p>
                <ul className="reminders-landing__features">
                    <li>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v10l4.5 4.5"/><circle cx="12" cy="12" r="10"/></svg>
                        Choose when to be reminded: 15 min, 1 hr, or 1 day before
                    </li>
                    <li>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                        SMS sent straight to your NZ mobile
                    </li>
                    <li>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        Completely free, no spam, cancel anytime
                    </li>
                </ul>
            </div>

            {/* Sign-up Form */}
            <div className="reminders-landing__form-card">
                {step === 'register' ? (
                    <>
                        <h2 className="reminders-landing__form-title">Sign up for reminders</h2>
                        <form onSubmit={handleRegisterSubmit}>
                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="landing-name">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    id="landing-name"
                                    className="rialto-form-input"
                                    placeholder="John Smith"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    required
                                    autoFocus
                                    autoComplete="name"
                                />
                            </div>

                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="landing-email">
                                    Email <span className="rialto-form-optional">(optional)</span>
                                </label>
                                <input
                                    type="email"
                                    id="landing-email"
                                    className="rialto-form-input"
                                    placeholder="john@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                />
                            </div>

                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="landing-phone">
                                    NZ Mobile Number
                                </label>
                                <input
                                    type="tel"
                                    id="landing-phone"
                                    className="rialto-form-input"
                                    placeholder="02x xxx xxxx"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    required
                                    autoComplete="tel"
                                />
                            </div>

                            {(localError || error) && (
                                <div className="rialto-form-error">
                                    {localError || error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="rialto-btn rialto-btn-primary"
                                disabled={loading || !isPhoneValid || !isNameValid}
                                style={{ width: '100%' }}
                            >
                                {loading ? 'Sending...' : 'Get Verification Code'}
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <h2 className="reminders-landing__form-title">Enter your code</h2>
                        <p className="reminders-landing__form-desc">
                            We've sent a 6-digit code to {phone}
                        </p>

                        <form onSubmit={handleCodeSubmit}>
                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="landing-code">
                                    Verification Code
                                </label>
                                <input
                                    type="text"
                                    id="landing-code"
                                    className="rialto-form-input reminders-landing__code-input"
                                    placeholder="123456"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    required
                                    autoFocus
                                    maxLength={6}
                                />
                            </div>

                            <div className="rialto-form-group">
                                <label className="rialto-form-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    Remember me for 30 days
                                </label>
                            </div>

                            {(localError || error) && (
                                <div className="rialto-form-error">
                                    {localError || error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="rialto-btn rialto-btn-primary"
                                disabled={loading || code.length !== 6}
                                style={{ width: '100%' }}
                            >
                                {loading ? 'Verifying...' : 'Verify & Sign In'}
                            </button>

                            <button
                                type="button"
                                className="rialto-btn rialto-btn-secondary"
                                onClick={() => { setStep('register'); setCode(''); setLocalError(''); }}
                                style={{ width: '100%', marginTop: '10px' }}
                            >
                                Use different number
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
